import { Injectable } from "@angular/core";
import { FormBuilder, FormGroup } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import * as Sentry from "@sentry/angular";
import { take, takeWhile, timer } from "rxjs";
import * as uuid from "uuid";
import { BrandingTypeEnum } from "../../model/forms/contract/form-template-contract";
import { FormInstance } from "../../model/forms/form-instance.model";
import { FormGroupCompanyService } from "../forms/form-group-company.service";
import { FormInstanceService } from "../forms/form-instance.service";
import { FormResponseService } from "../forms/form-response.service";
import { FormTemplateService } from "../forms/form-template.service";
import { ThemeService } from "../theme/theme.service";
import { ToastService } from "../toast.service";

export enum FormCompletionStates {
  Intro = "Intro",
  Progress = "Progress",
  ThankYou = "ThankYou",
}

@Injectable({
  providedIn: null,
})
export class FormCompletionService {
  public formGroup: FormGroup;
  public isLoadingFormInstance: boolean = true;
  public isSubmitting: boolean = false;
  public showErrors: boolean = false;
  public formInstance: FormInstance;
  public currentState: FormCompletionStates = FormCompletionStates.Intro;
  public imageSignedURL: string;

  private submissionUUID: string = uuid.v4();
  private formInstanceUUID: string | null;
  private formTimerSubscription: any;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private formInstanceService: FormInstanceService,
    private formResponseService: FormResponseService,
    private formBuilder: FormBuilder,
    private toastService: ToastService,
    private formGroupCompanyService: FormGroupCompanyService,
    private formTemplateService: FormTemplateService,
    public themeService: ThemeService
  ) {
    this._fetchFormInstance();
  }

  public startFormInstance() {
    this.currentState = FormCompletionStates.Progress;
    const formTimer = (this.formInstance.formTemplate.timerInMinutes || 0) * 60 * 1000;
    const alertBeforeEndFormTimer =
      (this.formInstance.formTemplate.alertBeforeEndTimerInMinutes || 0) * 60 * 1000;

    if (formTimer) {
      this.formTimerSubscription = timer(
        formTimer - alertBeforeEndFormTimer,
        alertBeforeEndFormTimer
      )
        .pipe(
          take(2),
          takeWhile(() => this.currentState !== FormCompletionStates.ThankYou)
        )
        .subscribe({
          next: (it) => {
            if (it === 0 && alertBeforeEndFormTimer) {
              this.toastService.showWarning(
                `Warning: You have only ${this.formInstance.formTemplate.alertBeforeEndTimerInMinutes} minutes remaining to finish this survey, or you will have to restart from the beginning.`,
                undefined
              );
            } else if (it === 1) {
              this.toastService.showWarning("Time expired. Please restart survey.");
              this.restartFormInstance();
            }
          },
        });
    }
  }

  public restartFormInstance() {
    if (this.formTimerSubscription) {
      this.formTimerSubscription.unsubscribe();
    }
    if (this.formGroup) {
      this.formGroup.reset();
    }
    this.currentState = FormCompletionStates.Intro;
    this.submissionUUID = uuid.v4();
    this.showErrors = false;
  }

  public async submitFormInstance() {
    this.showErrors = true;
    const formResponse = this.formInstance.collectAnswers(this.formGroup);

    if (!this.formGroup.valid) {
      return;
    }

    this.isSubmitting = true;

    if (this.formInstance.isPreviewData) {
      this.toastService.showInfo("This is preview data, responses will not be saved.");
      this.currentState = FormCompletionStates.ThankYou;
      this.isSubmitting = false;
      return;
    }

    try {
      await this.formResponseService.create({
        ...formResponse,
        submissionUUID: this.submissionUUID,
      });

      this.isSubmitting = false;
      this.formGroup.reset();
      this.currentState = FormCompletionStates.ThankYou;
    } catch (error) {
      this.isSubmitting = false;
      this.toastService.showError(error.message);
      Sentry.captureException(error);
    }
  }

  private async _fetchFormInstance() {
    this.formInstanceUUID = this.route.snapshot.paramMap.get("id");

    if (
      this.route.snapshot.url.length === 2 &&
      this.route.snapshot.url[0].path.toLowerCase() === "s1" &&
      !!this.route.snapshot.url[1]
    ) {
      // this URL has been redirected from an S1 URL.  We need to get the migrated FormInstanceId from the PSID in the URL
      this.formInstanceUUID = await this._extractFormInstanceUUIDFromS1PSID(
        this.route.snapshot.url[1].path
      );
    }

    if (!this.formInstanceUUID) {
      this.router.navigateByUrl("/404", { skipLocationChange: true });
    } else {
      this.isLoadingFormInstance = true;
      try {
        const formInstance = await this.formInstanceService.getFormInstanceByUUID(
          this.formInstanceUUID,
          false
        );
        if (!formInstance.canBeSubmitted) {
          // If the form instance is a placeholder for a CFET, we don't want to let the user fill it out
          this.router.navigateByUrl("/404", { skipLocationChange: true });
          return;
        }

        this._initFormInstance(formInstance);

        if (
          formInstance.formTemplate.brandingType === BrandingTypeEnum.Cobranding ||
          formInstance.formTemplate.brandingType === BrandingTypeEnum.WhileLabelling
        ) {
          await this._setSignedLogoUrls();
        }
        this.isLoadingFormInstance = false;
        await this._getImageSignUrl();

        // set themeing based on form group company
        this.themeService.setTheme(
          formInstance.formTemplate.brandingType,
          formInstance.formGroupInstance.primaryCompany
        );
      } catch (error) {
        if (error.statusCode === 404) {
          this.router.navigateByUrl("/404", { skipLocationChange: true });
        } else {
          Sentry.captureException(error);
        }
      }
    }
  }

  private _initFormInstance(formInstance: FormInstance) {
    this.formInstance = formInstance;
    this.formGroup = this.formBuilder.group(this.formInstance.formTemplate.collectFormControls());
    this.formGroup.reset();
  }

  /**
   * Set the signed logo URLs and background image URL for the formGroupCompany if one exists
   */
  private async _setSignedLogoUrls() {
    if (this.formInstance.formGroupInstance.primaryCompany) {
      await this.formInstance.formGroupInstance.primaryCompany.setSignedLogoUrls(
        this.formGroupCompanyService
      );
    }
  }

  private async _getImageSignUrl() {
    this.imageSignedURL =
      (await this.formTemplateService.getIntroImageSignedUrl(this.formInstance.formTemplateId)) ??
      "";
  }

  private async _extractFormInstanceUUIDFromS1PSID(s1URL: string): Promise<string> {
    let uuid = "";

    try {
      if (s1URL) {
        const s1URLParts = s1URL.split("-");
        if (s1URLParts.length >= 2) {
          const psid = parseInt(s1URLParts[1]);
          if (isNaN(psid)) {
            throw new Error(`Invalid PSID for ${s1URL}`);
          }

          uuid = await this.formInstanceService.getFormInstanceByS1PSID(psid);
        }
      }

      return uuid;
    } catch (error) {
      Sentry.captureException(error);
      return uuid;
    }
  }
}
